/** Notifications — everything this customer should know about their account.
 *
 * The server decides what is on the page and what each card says
 * (backend/chainlit/notices_api.py); this lists it. Open problems first, then
 * the rest by time, grouped Open / Today / Earlier. Opening the page clears the
 * bell's badge; a card stays highlighted until it is clicked.
 *
 * Socketless (App.tsx SOCKETLESS_ROUTES): opening it in its own tab must not
 * reserve a chat container. Built phone-first: on a phone the bell comes
 * straight here.
 */
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Empty } from '@/components/header/NoticesBell';
import { Header } from '@/components/header';
import { NoticeCard, useNoticeAction } from '@/components/notices/NoticeCard';
import {
  Notice,
  isToday,
  markRead,
  markSeen,
  refreshNotices,
  useNotices
} from '@/components/notices/api';
import { SidebarProvider } from '@/components/ui/sidebar';

import { useFeatures } from '@/hooks/useFeatures';

type Filter = 'all' | 'open' | 'reports';

const isReport = (n: Notice) => n.kind.startsWith('report_');
const isOpen = (n: Notice) => n.state === 'open';

export default function Notifications() {
  const { features, loading: flagsLoading } = useFeatures();
  const { items, loading } = useNotices();
  const [filter, setFilter] = useState<Filter>('all');
  const { run, dialog } = useNoticeAction();

  useEffect(() => {
    refreshNotices().then(markSeen);
  }, []);

  const shown = useMemo(() => {
    const list = items ?? [];
    if (filter === 'open') return list.filter(isOpen);
    if (filter === 'reports') return list.filter(isReport);
    return list;
  }, [items, filter]);

  if (!flagsLoading && !features.notices) return <Navigate replace to="/" />;

  const openCount = (items ?? []).filter(isOpen).length;
  const hasReports = (items ?? []).some(isReport);
  // Still-open stories first, whatever their age; then the rest by day.
  const ongoing = shown.filter(isOpen);
  const today = shown.filter((n) => !isOpen(n) && isToday(n.at));
  const earlier = shown.filter((n) => !isOpen(n) && !isToday(n.at));

  const chip = (key: Filter, label: string) => (
    <button
      type="button"
      onClick={() => setFilter(key)}
      aria-pressed={filter === key}
      className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        filter === key
          ? 'bg-foreground text-background'
          : 'bg-muted text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full flex-col">
        <Header />
        <div className="flex-1 overflow-auto">
          <div className="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-8">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <h1 className="hidden text-xl font-semibold sm:block">
                  Notifications
                </h1>
                <p className="text-sm text-muted-foreground">
                  Reports, data updates and anything that needs you.
                </p>
              </div>
              {items?.some((i) => i.unread) ? (
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-primary hover:underline"
                  onClick={() => markRead('all')}
                >
                  Mark all as read
                </button>
              ) : null}
            </div>

            <div className="mb-3 flex gap-2 overflow-x-auto">
              {chip('all', 'All')}
              {openCount ? chip('open', `Open · ${openCount}`) : null}
              {hasReports ? chip('reports', 'Reports') : null}
            </div>

            <div className="overflow-hidden rounded-xl border bg-card">
              {items === null ? (
                <div className="grid place-items-center py-16">
                  {loading ? (
                    <Loader2 className="size-5 animate-spin text-muted-foreground" />
                  ) : (
                    <Empty />
                  )}
                </div>
              ) : shown.length === 0 ? (
                <Empty />
              ) : (
                <>
                  <Group title="Open" items={ongoing} onAction={run} />
                  <Group title="Today" items={today} onAction={run} />
                  <Group title="Earlier" items={earlier} onAction={run} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {dialog}
    </SidebarProvider>
  );
}

function Group({
  title,
  items,
  onAction
}: {
  title: string;
  items: Notice[];
  onAction: Parameters<typeof NoticeCard>[0]['onAction'];
}) {
  if (!items.length) return null;
  return (
    <section>
      <h2 className="border-b bg-muted/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      <div className="divide-y">
        {items.map((n) => (
          <NoticeCard key={n.id} n={n} onAction={onAction} />
        ))}
      </div>
    </section>
  );
}
